import React, { useMemo, useState } from "react";
import { AbsoluteFill, Img, staticFile, useCurrentFrame, interpolate, Easing } from "remotion";
import { z } from "zod";

// Zod schema for FourPanelReveal props
export const FourPanelRevealPropsSchema = z.object({
  imageSrc: z.string().default("1.jpeg"),
  // Timing controls
  phase1Start: z.number().default(30), // When TL and BR appear (blurred)
  phase1Duration: z.number().default(30),
  phase2Start: z.number().default(90), // When TR and BL appear (blurred)
  phase2Duration: z.number().default(30),
  phase3Start: z.number().default(150), // When sharp reveals begin
  phase3Interval: z.number().default(30), // Frames between each reveal
  phase3Duration: z.number().default(20),
  // Visual effects
  blurAmount: z.number().min(0).max(20).default(10),
  pulseScale: z.number().min(1).max(2).default(1.2),
  flashIntensity: z.number().min(0).max(1).default(0.8),
  flashDuration: z.number().min(1).max(15).default(8),
});

/**
 * FourPanelReveal Component
 * 
 * Animation sequence:
 * 1. Phase 1: TL and BR quadrants appear with blur + pulse
 * 2. Phase 2: TR and BL quadrants appear with blur + pulse
 * 3. Phase 3: Each quadrant becomes sharp in sequence (TL → BR → TR → BL) with pulse + flash
 * 
 * Each quadrant shows a different portion of the same image using CSS clip-path and object-position
 */
export const FourPanelReveal = ({
  imageSrc = "1.jpeg",
  phase1Start = 30,
  phase1Duration = 30,
  phase2Start = 90,
  phase2Duration = 30,
  phase3Start = 150,
  phase3Interval = 30,
  phase3Duration = 20,
  blurAmount = 10,
  pulseScale = 1.2,
  flashIntensity = 0.8,
  flashDuration = 8,
}) => {
  const frame = useCurrentFrame();

  // Track image error state
  const [imageError, setImageError] = useState(false);
  const [imageErrorMessage, setImageErrorMessage] = useState('');

  // Memoize the image source to avoid recalculating on every frame
  const imageSource = useMemo(() => {
    return imageSrc.startsWith('http') ? imageSrc : staticFile(imageSrc);
  }, [imageSrc]);

  // Handle image errors
  const handleImageError = (error) => {
    console.error('Image load error:', error);
    setImageError(true);
    setImageErrorMessage(error?.message || 'Failed to load image');
  };

  // Memoize quadrant definitions (these never change)
  const quadrants = useMemo(() => [
    {
      id: 'TL',
      clipPath: 'polygon(0% 0%, 50% 0%, 50% 50%, 0% 50%)',
      backgroundPosition: '0% 0%',
      backgroundSize: '200% 200%',
      borderStyle: 'border-right: 1px solid black; border-bottom: 1px solid black;',
      phase1: true,
      phase3Order: 0,
    },
    {
      id: 'TR', 
      clipPath: 'polygon(50% 0%, 100% 0%, 100% 50%, 50% 50%)',
      backgroundPosition: '100% 0%',
      backgroundSize: '200% 200%',
      borderStyle: 'border-left: 1px solid black; border-bottom: 1px solid black;',
      phase1: false,
      phase3Order: 2,
    },
    {
      id: 'BL',
      clipPath: 'polygon(0% 50%, 50% 50%, 50% 100%, 0% 100%)',
      backgroundPosition: '0% 100%',
      backgroundSize: '200% 200%',
      borderStyle: 'border-right: 1px solid black; border-top: 1px solid black;',
      phase1: false,
      phase3Order: 3,
    },
    {
      id: 'BR',
      clipPath: 'polygon(50% 50%, 100% 50%, 100% 100%, 50% 100%)',
      backgroundPosition: '100% 100%',
      backgroundSize: '200% 200%',
      borderStyle: 'border-left: 1px solid black; border-top: 1px solid black;',
      phase1: true,
      phase3Order: 1,
    },
  ], []);

  // Pre-calculate which quadrants should be visible to avoid unnecessary calculations
  const visibleQuadrants = useMemo(() => {
    const visible = [];
    
    // Phase 1 quadrants (TL, BR) are visible after phase1Start
    if (frame >= phase1Start) {
      visible.push(quadrants[0], quadrants[3]); // TL, BR
    }
    
    // Phase 2 quadrants (TR, BL) are visible after phase2Start
    if (frame >= phase2Start) {
      visible.push(quadrants[2], quadrants[1]); // TR, BL
    }
    
    return visible;
  }, [frame, phase1Start, phase2Start, quadrants]);

  // Optimized state calculation - only calculate for visible quadrants
  const getQuadrantState = useMemo(() => {
    const cache = {};
    
    return (quadrant) => {
      // Use cache to avoid recalculating identical states
      const cacheKey = `${quadrant.id}-${frame}`;
      if (cache[cacheKey]) return cache[cacheKey];
      
      const state = {
        opacity: 0,
        blur: 0,
        scale: 1,
        flash: 0,
      };

      // Phase 1: TL and BR appear with blur
      if (quadrant.phase1 && frame >= phase1Start) {
        const progress = Math.min((frame - phase1Start) / Math.max(1, phase1Duration), 1);
        
        // Use simpler easing for better performance
        state.opacity = progress;
        state.blur = blurAmount;
        
        // Simplified pulse calculation
        const pulseProgress = Math.sin(progress * Math.PI);
        state.scale = 1 + (pulseScale - 1) * pulseProgress * 0.5;
      }

      // Phase 2: TR and BL appear with blur
      if (!quadrant.phase1 && frame >= phase2Start) {
        const progress = Math.min((frame - phase2Start) / Math.max(1, phase2Duration), 1);
        
        state.opacity = progress;
        state.blur = blurAmount;
        
        // Simplified pulse calculation
        const pulseProgress = Math.sin(progress * Math.PI);
        state.scale = 1 + (pulseScale - 1) * pulseProgress * 0.5;
      }

      // Phase 3: Sharp reveals in sequence
      const revealStart = phase3Start + (quadrant.phase3Order * phase3Interval);
      if (frame >= revealStart && state.opacity > 0) {
        const progress = Math.min((frame - revealStart) / Math.max(1, phase3Duration), 1);
        
        // Linear blur removal (faster than interpolate)
        state.blur = blurAmount * (1 - progress);
        
        // Simplified pulse for phase 3
        const pulseProgress = Math.sin(progress * Math.PI);
        state.scale = 1 + (pulseScale * 1.1 - 1) * pulseProgress;
        
        // Simplified flash
        if (frame < revealStart + flashDuration) {
          const flashProgress = (frame - revealStart) / Math.max(1, flashDuration);
          state.flash = flashIntensity * Math.max(0, 1 - flashProgress);
        }
      }
      
      // Cache the result
      cache[cacheKey] = state;
      return state;
    };
  }, [frame, phase1Start, phase1Duration, phase2Start, phase2Duration, phase3Start, phase3Interval, phase3Duration, blurAmount, pulseScale, flashIntensity, flashDuration]);

  return (
    <AbsoluteFill style={{ backgroundColor: "#000" }}>
      {imageError ? (
        // Show error fallback instead of crashing
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#000",
            color: "#fff",
            fontFamily: "Arial, sans-serif",
            padding: "40px",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 20 }}>⚠️</div>
          <div style={{ fontSize: 24, marginBottom: 10 }}>Image Error</div>
          <div style={{ fontSize: 16, color: "#999", maxWidth: 600 }}>
            {imageErrorMessage || 'Unable to load image'}
          </div>
          <div style={{ fontSize: 14, color: "#666", marginTop: 20 }}>
            Source: {imageSrc}
          </div>
        </div>
      ) : (
        <>
          {/* Only render visible quadrants */}
          {visibleQuadrants.map((quadrant) => {
            const state = getQuadrantState(quadrant);
            
            return (
              <div
                key={quadrant.id}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  opacity: state.opacity,
                  transform: `scale3d(${state.scale}, ${state.scale}, 1)`,
                  transformOrigin: "center center",
                  filter: state.blur > 0 ? `blur(${state.blur}px)` : 'none',
                  clipPath: quadrant.clipPath,
                  overflow: "hidden",
                  willChange: state.opacity < 1 || state.scale !== 1 || state.blur > 0 ? 'transform, opacity, filter' : 'auto',
                }}
              >
                {/* Image showing the correct quadrant portion */}
                <Img
                  src={imageSource}
                  onError={handleImageError}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transform: quadrant.imageTransform,
                    pointerEvents: "none",
                  }}
                />
                
                {/* White flash overlay - only render when needed */}
                {state.flash > 0 && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      backgroundColor: `rgba(255, 255, 255, ${state.flash})`,
                      pointerEvents: "none",
                    }}
                  />
                )}
                

              </div>
            );
          })}

          {/* Interior borders overlay - positioned outside clipped containers */}
          {visibleQuadrants.length > 0 && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
                zIndex: 5,
              }}
            >
              {/* Vertical center line */}
              <div
                style={{
                  position: "absolute",
                  left: "50%",
                  top: 0,
                  width: "1px",
                  height: "100%",
                  backgroundColor: "black",
                  transform: "translateX(-0.5px)",
                }}
              />
              {/* Horizontal center line */}
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: 0,
                  width: "100%",
                  height: "1px",
                  backgroundColor: "black",
                  transform: "translateY(-0.5px)",
                }}
              />
            </div>
          )}
        </>
      )}
    </AbsoluteFill>
  );
};

export default FourPanelReveal;

;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
